import React, { useState, useEffect, useRef } from 'react';
import { supabase } from './supabaseClient';
import { X, Send, MessageCircle } from 'lucide-react';

const ChatModal = ({ 
  isOpen, 
  onClose, 
  projectId,       // для фільтрації по проєкту (null = всі)
  userId,          // ID юзера (менеджера проєкту)
  projects,        // масив проєктів (для назв)
  isClientMode = false,  // true = клієнтський в'ю
  shareToken = null      // для клієнта
}) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [clientName, setClientName] = useState('');
  const [nameConfirmed, setNameConfirmed] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Завантажуємо ім'я з localStorage
  useEffect(() => {
    if (isClientMode && shareToken) {
      const savedName = localStorage.getItem(`lihtar_client_name_${shareToken}`);
      if (savedName) {
        setClientName(savedName);
        setNameConfirmed(true);
      }
    }
  }, [isClientMode, shareToken]);

  // Блокуємо скрол body при відкритій модалці
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      loadMessages();
      const interval = setInterval(() => {
        loadMessages(true);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [isOpen, projectId]);

  const prevMessageCount = useRef(0);

  useEffect(() => {
    if (messages.length > prevMessageCount.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: prevMessageCount.current === 0 ? 'auto' : 'smooth' });
    }
    prevMessageCount.current = messages.length;
  }, [messages]);

  const loadMessages = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      let query = supabase
        .from('share_comments')
        .select('*')
        .order('created_at', { ascending: true });

      if (isClientMode && shareToken) {
        query = query.eq('share_token', shareToken);
      } else if (projectId) {
        query = query.eq('project_id', String(projectId));
      } else if (userId) {
        // Всі коментарі юзера — через shared_projects
        const { data: shares } = await supabase
          .from('shared_projects')
          .select('share_token, project_id')
          .eq('user_id', userId);
        
        if (shares && shares.length > 0) {
          const tokens = shares.map(s => s.share_token);
          query = query.in('share_token', tokens);
        } else {
          setMessages([]);
          setLoading(false);
          return;
        }
      }

      const { data, error } = await query;
      if (error) throw error;
      setMessages(data || []);

      // Позначаємо як прочитані
      if (!isClientMode && data && data.length > 0) {
        const unread = data.filter(m => m.is_from_client && !m.is_read).map(m => m.id);
        if (unread.length > 0) {
          await supabase
            .from('share_comments')
            .update({ is_read: true })
            .in('id', unread);
        }
      }
    } catch (e) {
      console.error('Помилка завантаження повідомлень:', e);
    }
    setLoading(false);
  };

  const sendMessage = async () => {
    const text = newMessage.trim();
    if (!text) return;
    if (isClientMode && !nameConfirmed) return;

    setSending(true);
    try {
      let token = shareToken;
      let projId = projectId;

      if (!isClientMode && projectId) {
        // Знаходимо share_token для проєкту
        const { data: share } = await supabase
          .from('shared_projects')
          .select('share_token')
          .eq('user_id', userId)
          .eq('project_id', String(projectId))
          .single();
        
        if (!share) {
          if (window.showToast) window.showToast('Спочатку створіть посилання для клієнта');
          setSending(false);
          return;
        }
        token = share.share_token;
      }

      const insertData = {
        share_token: token,
        project_id: String(projId || projectId),
        message: text,
        is_from_client: isClientMode,
        is_read: !isClientMode,
        client_name: isClientMode ? (clientName.trim() || null) : null
      };
      
      console.log('Sending message:', insertData);
      
      if (!token) {
        console.error('No share_token available');
        if (window.showToast) window.showToast('Спочатку створіть посилання для клієнта');
        setSending(false);
        return;
      }

      const { error } = await supabase.from('share_comments').insert(insertData);

      if (error) {
        console.error('Supabase insert error:', error);
        throw error;
      }
      
      setNewMessage('');
      // Зберігаємо ім'я в localStorage
      if (isClientMode && clientName.trim() && shareToken) {
        localStorage.setItem(`lihtar_client_name_${shareToken}`, clientName.trim());
      }
      await loadMessages();
      
      setTimeout(() => inputRef.current?.focus(), 100);
    } catch (e) {
      console.error('Помилка відправки:', e);
    }
    setSending(false);
  };

  const getProjectName = (projId) => {
    if (!projects) return '';
    const p = projects.find(pr => String(pr.id) === String(projId));
    return p?.name || '';
  };

  const formatTime = (dateStr) => {
    const d = new Date(dateStr);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = d.toDateString() === yesterday.toDateString();
    
    const time = d.toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' });
    if (isToday) return time;
    if (isYesterday) return `Вчора ${time}`;
    return `${d.toLocaleDateString('uk-UA', { day: '2-digit', month: '2-digit' })} ${time}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 px-0 sm:px-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} style={{ touchAction: 'none' }}>
      <div className="bg-white rounded-t-2xl sm:rounded-2xl sm:max-w-lg w-full shadow-2xl overflow-hidden flex flex-col max-h-[85vh] sm:max-h-[80vh]" style={{ height: isClientMode ? '70vh' : '75vh' }}>
        {/* Header */}
        <div className="px-4 sm:px-5 py-3 border-b flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5" style={{ color: '#163248' }} />
            <h3 className="text-base font-semibold" style={{ color: '#163248' }}>
              {isClientMode ? 'Повідомлення' : projectId ? 'Повідомлення клієнта' : 'Всі повідомлення'}
            </h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-5 py-3 space-y-3" style={{ overscrollBehavior: 'contain' }}>
          {loading ? (
            <div className="text-center py-8">
              <div className="w-6 h-6 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin mx-auto mb-2"></div>
              <p className="text-xs text-gray-400">Завантаження...</p>
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-8">
              <MessageCircle className="w-10 h-10 text-gray-200 mx-auto mb-2" />
              <p className="text-sm text-gray-400">
                {isClientMode 
                  ? 'Напишіть повідомлення менеджеру проєкту'
                  : 'Поки що повідомлень немає'}
              </p>
            </div>
          ) : (
            messages.map((msg) => {
              const isMyMessage = isClientMode 
                ? (msg.is_from_client && msg.client_name === clientName)
                : !msg.is_from_client;
              const isOtherClient = isClientMode && msg.is_from_client && msg.client_name !== clientName;
              const showProjectName = !projectId && !isClientMode;
              
              return (
                <div key={msg.id} className={`flex ${isMyMessage ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] ${isMyMessage ? 'order-1' : 'order-0'}`}>
                    {/* Ім'я + проєкт */}
                    {!isMyMessage && (
                      <div className="flex items-center gap-1.5 mb-0.5 px-1">
                        <span className="text-[10px] font-medium" style={{ color: msg.is_from_client ? '#f59e0b' : '#163248' }}>
                          {msg.is_from_client ? (msg.client_name || 'Клієнт') : (isClientMode ? 'Менеджер' : 'Ви')}
                        </span>
                        {showProjectName && (
                          <span className="text-[10px] text-gray-400">• {getProjectName(msg.project_id)}</span>
                        )}
                      </div>
                    )}
                    {isMyMessage && showProjectName && (
                      <div className="text-right mb-0.5 px-1">
                        <span className="text-[10px] text-gray-400">{getProjectName(msg.project_id)}</span>
                      </div>
                    )}
                    {/* Bubble */}
                    <div className={`rounded-2xl px-3.5 py-2 ${
                      isMyMessage 
                        ? 'rounded-tr-md text-white' 
                        : 'rounded-tl-md bg-gray-100'
                    }`}
                      style={isMyMessage ? { backgroundColor: '#163248' } : {}}
                    >
                      <p className={`text-sm whitespace-pre-wrap break-words ${isMyMessage ? 'text-white' : 'text-gray-800'}`}>
                        {msg.message}
                      </p>
                      <p className={`text-[10px] mt-1 ${isMyMessage ? 'text-white/50 text-right' : 'text-gray-400'}`}>
                        {formatTime(msg.created_at)}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Ім'я клієнта (обов'язково, один раз) */}
        {isClientMode && !nameConfirmed && (
          <div className="px-4 sm:px-5 py-4 border-t bg-gray-50">
            <p className="text-xs text-gray-500 mb-2">Вкажіть ваше ім'я для початку листування</p>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && clientName.trim()) {
                    setNameConfirmed(true);
                    localStorage.setItem(`lihtar_client_name_${shareToken}`, clientName.trim());
                    setTimeout(() => inputRef.current?.focus(), 100);
                  }
                }}
                placeholder="Ваше ім'я"
                className="flex-1 px-3 py-2 border rounded-lg bg-white"
                style={{ fontSize: '16px' }}
                autoFocus
              />
              <button
                onClick={() => {
                  if (clientName.trim()) {
                    setNameConfirmed(true);
                    localStorage.setItem(`lihtar_client_name_${shareToken}`, clientName.trim());
                    setTimeout(() => inputRef.current?.focus(), 100);
                  }
                }}
                disabled={!clientName.trim()}
                className="px-4 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-30"
                style={{ backgroundColor: '#163248' }}
              >
                Готово
              </button>
            </div>
          </div>
        )}

        {/* Input */}
        {(!isClientMode || nameConfirmed) && (
        <div className="px-4 sm:px-5 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))] border-t flex-shrink-0 bg-white">
          <div className="flex items-end gap-2">
            <textarea
              ref={inputRef}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              placeholder={isClientMode ? 'Напишіть повідомлення...' : 'Відповісти...'}
              className="flex-1 px-3 py-2 border rounded-xl resize-none bg-gray-50 focus:bg-white focus:border-gray-300 transition-colors"
              rows={1}
              style={{ maxHeight: '80px', fontSize: '16px' }}
            />
            <button
              onClick={sendMessage}
              disabled={!newMessage.trim() || sending}
              className="p-2.5 rounded-xl text-white disabled:opacity-30 transition-opacity flex-shrink-0"
              style={{ backgroundColor: '#163248' }}
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default ChatModal;
